import React, { useState, useEffect, useRef } from 'react';
import FormSection from '../FormSection';
import FormField from '../FormField';
import Input from '../Input';
import Textarea from '../Textarea';
import CheckboxGroup from '../CheckboxGroup';
import { ParticipationModality, EventProgramParticipation } from '../../types/profile.types';
import { participationModalities, participationModalityTooltips, eventProgramParticipation } from '../../data/profileData';
import '../CheckboxGroup.css';
import './ThematicAxesSection.css';
interface ParticipationModalitySectionProps {
  selectedModalities: ParticipationModality[];
  onChange: (modalities: ParticipationModality[]) => void;
  agreedModality?: string;
  modalityObservations?: string;
  error?: string;
  readOnly?: boolean;
  eventProgramParticipation?: EventProgramParticipation[];
  onEventProgramParticipationChange?: (values: string[]) => void;
  eventProgramParticipationError?: string;
  requestInformation?: boolean;
  onRequestInformationChange?: (value: boolean) => void;
  /** Cuando true, la sección se usa en el modal "Crear contacto" del CRM; los cambios solo aplican aquí y no en el perfil privado */
  createContactMode?: boolean;
}
const ParticipationModalitySection: React.FC<ParticipationModalitySectionProps> = ({ 
  selectedModalities, 
  onChange, 
  agreedModality,
  modalityObservations,
  error,
  readOnly = false,
  createContactMode = false,
  eventProgramParticipation: selectedEventProgramParticipation = [],
  onEventProgramParticipationChange,
  eventProgramParticipationError,
  requestInformation = false,
  onRequestInformationChange
}) => {
  const [visibleTooltip, setVisibleTooltip] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ left: number; top: number } | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  // Detectar si es mobile o tablet
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileOrTablet(window.innerWidth <= 1024);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  // Efecto para manejar el tooltip en el DOM
  useEffect(() => {
    // Limpiar tooltip existente primero
    if (tooltipRef.current && tooltipRef.current.parentNode) {
      tooltipRef.current.parentNode.removeChild(tooltipRef.current);
      tooltipRef.current = null;
    }
    if (visibleTooltip !== null && tooltipPosition !== null) {
      const modality = participationModalities[visibleTooltip];
      const tooltipInfo = participationModalityTooltips[modality.value];
      if (!tooltipInfo || (!tooltipInfo.dirigidoA && tooltipInfo.beneficios.length === 0)) return;
      // Detectar si está en modo oscuro
      const isDarkMode = document.documentElement.classList.contains('dark-theme');
      // Crear el elemento del tooltip
      const tooltipDiv = document.createElement('div');
      tooltipDiv.className = 'tooltip-content';
      tooltipDiv.style.cssText = `
        position: fixed !important;
        left: ${tooltipPosition.left}px !important;
        top: ${tooltipPosition.top}px !important;
        z-index: 999999 !important;
        background-color: ${isDarkMode ? '#0f172a' : '#ffffff'} !important;
        border: 2px solid #CE2B15 !important;
        border-radius: 8px !important;
        padding: 12px 16px !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        min-width: 280px !important;
        max-width: 400px !important;
        font-family: Montserrat, sans-serif !important;
        pointer-events: none !important;
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
      `;
      // Crear el contenido
      if (tooltipInfo.dirigidoA) {
        const dirigidoADiv = document.createElement('div');
        dirigidoADiv.style.cssText = `font-weight: 700; color: ${isDarkMode ? '#ffffff' : '#1e293b'}; font-size: 13px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid ${isDarkMode ? '#334155' : '#e5e7eb'};`;
        dirigidoADiv.textContent = 'Dirigido a:';
        tooltipDiv.appendChild(dirigidoADiv);
        const dirigidoAText = document.createElement('div');
        dirigidoAText.style.cssText = `font-size: 12px; color: ${isDarkMode ? '#cbd5e1' : '#64748b'}; line-height: 1.6; margin-bottom: 12px;`;
        dirigidoAText.textContent = tooltipInfo.dirigidoA;
        tooltipDiv.appendChild(dirigidoAText);
      }
      if (tooltipInfo.beneficios.length > 0) {
        const beneficiosDiv = document.createElement('div');
        beneficiosDiv.style.cssText = `font-weight: 700; color: ${isDarkMode ? '#ffffff' : '#1e293b'}; font-size: 13px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid ${isDarkMode ? '#334155' : '#e5e7eb'};`;
        beneficiosDiv.textContent = 'Beneficios:';
        tooltipDiv.appendChild(beneficiosDiv);
        const ul = document.createElement('ul');
        ul.style.cssText = 'list-style: none; padding: 0; margin: 0;';
        tooltipInfo.beneficios.forEach((beneficio) => {
          const li = document.createElement('li');
          li.style.cssText = `font-size: 12px; color: ${isDarkMode ? '#cbd5e1' : '#64748b'}; line-height: 1.6; padding: 4px 0; padding-left: 16px; position: relative;`;
          const bullet = document.createElement('span');
          bullet.style.cssText = 'position: absolute; left: 0; color: #CE2B15; font-weight: bold;';
          bullet.textContent = '•';
          li.appendChild(bullet);
          li.appendChild(document.createTextNode(beneficio));
          ul.appendChild(li);
        });
        tooltipDiv.appendChild(ul);
      }
      if (document.body) {
        document.body.appendChild(tooltipDiv);
        tooltipRef.current = tooltipDiv;
      }
      // Cleanup
      return () => {
        if (tooltipRef.current && tooltipRef.current.parentNode) {
          tooltipRef.current.parentNode.removeChild(tooltipRef.current);
          tooltipRef.current = null;
        }
      };
    }
  }, [visibleTooltip, tooltipPosition]);
  // Cerrar tooltip al hacer clic fuera (solo en mobile/tablet)
  useEffect(() => {
    if (!isMobileOrTablet || visibleTooltip === null) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (!target.closest('.checkbox-item')) {
          setVisibleTooltip(null);
          setTooltipPosition(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileOrTablet, visibleTooltip]);
  const handleChange = (value: ParticipationModality, checked: boolean) => {
    if (checked) {
      onChange([...selectedModalities, value]);
    } else {
      onChange(selectedModalities.filter(v => v !== value));
    }
  };
  return (
    <FormSection 
      title={createContactMode ? undefined : 'Modalidad de participación'} 
      description={createContactMode ? undefined : (
        <>
          Conoce los beneficios de cada modalidad en:{' '}
          <a 
            href="https://smartcityexposantiagodelestero.com/socios-empresariales/" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              color: '#CE2B15', 
              textDecoration: 'none',
              fontWeight: 600
            }}
            onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
          >
            Socios Empresariales
          </a>
          {', '}
          <a 
            href="https://smartcityexposantiagodelestero.com/instituciones-colaboradoras/" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              color: '#CE2B15', 
              textDecoration: 'none',
              fontWeight: 600
            }}
            onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
          >
            Instituciones Colaboradoras
          </a>
          {' y '}
          <a 
            href="https://smartcityexposantiagodelestero.com/gobiernos-que-innovan/" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              color: '#CE2B15', 
              textDecoration: 'none',
              fontWeight: 600
            }}
            onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
          >
            Gobiernos que Innovan
          </a>
        </>
      )}
    >
      <FormField
        label="Participación en el programa del evento"
        required={!createContactMode}
        error={eventProgramParticipationError}
        helpText={createContactMode ? undefined : '(Seleccionar las opciones de interés)'}
      >
        <CheckboxGroup
          options={eventProgramParticipation}
          selectedValues={selectedEventProgramParticipation || []}
          onChange={(values) => {
            if (onEventProgramParticipationChange) {
              onEventProgramParticipationChange(values);
            }
          }}
          error={!!eventProgramParticipationError}
          readOnly={readOnly}
        />
      </FormField>
      <FormField 
        label="Modalidades de asociación" 
        required={!createContactMode}
        error={error}
        helpText={createContactMode ? undefined : (
          <span style={{
            fontSize: '14px',
            color: '#64748b',
            fontFamily: "'Montserrat', sans-serif",
            lineHeight: '1.5',
            fontStyle: 'normal'
          }}>
            En caso de aún no haber establecido tu modalidad de asociación, indica las que son de tu interés para recibir más información
          </span>
        )}
      >
        <div className={`checkbox-group ${error ? 'error' : ''}`}>
          {participationModalities.map((option, index) => {
            const tooltipInfo = participationModalityTooltips[option.value];
            const hasTooltip = tooltipInfo && (tooltipInfo.dirigidoA || tooltipInfo.beneficios.length > 0);
            return (
              <label 
                key={option.value} 
                className={`checkbox-item ${hasTooltip ? 'has-tooltip' : ''}`}
                onMouseEnter={(e) => {
                  // En desktop: mostrar tooltip al hacer hover
                  if (hasTooltip && !isMobileOrTablet) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const tooltipWidth = 400;
                    const tooltipHeight = 350; // Altura estimada del tooltip
                    const spacing = 10;
                    const padding = 10; // Padding mínimo desde los bordes
                    // Calcular posición horizontal (centrado por defecto)
                    let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                    // Ajustar si se sale por la derecha
                    if (left + tooltipWidth > window.innerWidth - padding) {
                      left = window.innerWidth - tooltipWidth - padding;
                    }
                    // Ajustar si se sale por la izquierda
                    if (left < padding) {
                      left = padding;
                    }
                    // Calcular posición vertical (debajo por defecto)
                    let top = rect.bottom + spacing;
                    // Si no cabe abajo, intentar arriba
                    if (top + tooltipHeight > window.innerHeight - padding) {
                      top = rect.top - tooltipHeight - spacing;
                      // Si tampoco cabe arriba, centrar verticalmente
                      if (top < padding) {
                        top = Math.max(padding, (window.innerHeight - tooltipHeight) / 2);
                        // Si aún así no cabe, ajustar a la altura disponible
                        if (top + tooltipHeight > window.innerHeight - padding) {
                          top = window.innerHeight - tooltipHeight - padding;
                        }
                      }
                    }
                    // Asegurar que siempre esté dentro de los límites
                    top = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding));
                    left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding));
                    setVisibleTooltip(index);
                    setTooltipPosition({ left, top });
                  }
                }}
                onMouseLeave={() => {
                  // En desktop: ocultar tooltip al salir del hover
                  if (!isMobileOrTablet) {
                    setVisibleTooltip(null);
                    setTooltipPosition(null);
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedModalities.includes(option.value)}
                  onChange={(e) => handleChange(option.value, e.target.checked)}
                  disabled={readOnly}
                />
                <span className="checkbox-label">{option.label}</span>
                {hasTooltip && isMobileOrTablet && (
                  <span
                    className="modalidad-info-icon"
                    style={{
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#f3f4f6',
                      color: '#6b7280',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      border: '1px solid #d1d5db',
                      flexShrink: 0,
                      transition: 'all 0.2s ease',
                      marginLeft: '8px',
                      display: 'flex'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      // Si el tooltip ya está visible para esta modalidad, cerrarlo
                      if (visibleTooltip === index) {
                        setVisibleTooltip(null);
                        setTooltipPosition(null);
                        return;
                      }
                      const rect = e.currentTarget.getBoundingClientRect();
                      const tooltipWidth = 400;
                      const tooltipHeight = 350; // Altura estimada del tooltip
                      const spacing = 10;
                      const padding = 10; // Padding mínimo desde los bordes
                      // Calcular posición horizontal (centrado por defecto)
                      let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                      // Ajustar si se sale por la derecha
                      if (left + tooltipWidth > window.innerWidth - padding) {
                        left = window.innerWidth - tooltipWidth - padding;
                      }
                      // Ajustar si se sale por la izquierda
                      if (left < padding) {
                        left = padding;
                      }
                      // Calcular posición vertical (debajo por defecto)
                      let top = rect.bottom + spacing;
                      // Si no cabe abajo, intentar arriba
                      if (top + tooltipHeight > window.innerHeight - padding) {
                        top = rect.top - tooltipHeight - spacing;
                        // Si tampoco cabe arriba, centrar verticalmente
                        if (top < padding) {
                          top = Math.max(padding, (window.innerHeight - tooltipHeight) / 2);
                          // Si aún así no cabe, ajustar a la altura disponible
                          if (top + tooltipHeight > window.innerHeight - padding) {
                            top = window.innerHeight - tooltipHeight - padding;
                          }
                        }
                      }
                      // Asegurar que siempre esté dentro de los límites
                      top = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding));
                      left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding));
                      setVisibleTooltip(index);
                      setTooltipPosition({ left, top });
                    }}
                  >
                    ?
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </FormField>
      {/* Checkbox: Solicitar envío de información - oculto en modal crear contacto */}
      {!createContactMode && (
      <div style={{ marginTop: '4px', marginBottom: '24px' }}>
        <div className="checkbox-group">
          <label className="checkbox-item" style={{ alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={requestInformation || false}
              onChange={(e) => {
                if (onRequestInformationChange && !readOnly) {
                  onRequestInformationChange(e.target.checked);
                }
              }}
              disabled={readOnly}
            />
            <span className="checkbox-label" style={{ marginTop: '2px' }}>Solicitar envío de información</span>
          </label>
        </div>
      </div>
      )}
      {/* Modalidad acordada - oculto en modal crear contacto */}
      {!createContactMode && (
      <FormField label="Modalidad acordada">
        <Input
          value={(() => {
            if (!agreedModality) return '';
            // Buscar la modalidad en el array para obtener su label
            const modalityOption = participationModalities.find(m => m.value === agreedModality);
            return modalityOption ? modalityOption.label : agreedModality;
          })()}
          placeholder="Aquí aparecerá la modalidad acordada con la organización una vez confirmada"
          disabled={readOnly || true}
        />
      </FormField>
      )}
      {/* Observaciones - oculto en modal crear contacto */}
      {!createContactMode && (
      <FormField label="Observaciones">
        <Textarea
          value={modalityObservations || ''}
          placeholder="Observaciones de la modalidad acordada"
          disabled={readOnly || true}
          rows={4}
        />
      </FormField>
      )}
    </FormSection>
  );
};
export default ParticipationModalitySection;
